import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom'; // AXIS148: URL persistence
import { FollowUpComposer } from '../components/FollowUpComposer';
import { ReferralDetailModal } from '../components/ReferralDetailModal';
import { ReferralFilterPanel } from '../components/ReferralFilterPanel';
import { Flex } from '../components/Flex';
import { Button } from '../components/Button';
import { ActionMenu } from '../components/ActionMenu';
import { TabMenu } from '../components/TabMenu';
import { TabMenuItem } from '../components/TabMenuItem';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { SRCompleteResponseModal, SRCompleteResponsePayload } from '../components/SRCompleteResponseModal';
import { SRClarificationModal } from '../components/SRClarificationModal';
import {
  ApiService,
  Referral,
  PaginatedReferrals,
  User,
  FilterConfig,
  isSrReferral,
  isSrReferralCancelled,
  canMarkCompleteReferral,
} from '../services/apiService';
import { AuthService } from '../services/authService';
import { TriageCategoryBadge } from '../components/TriageCategoryBadge';
import { FilterChips } from '../components/FilterChips'; // AXIS148
import { UserService } from '../services/userService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import FeatherIcon from 'feather-icons-react';
import { SourceBadge } from '../components/SourceBadge';
import { SimulationBadge } from '../components/SimulationBadge';
import { SrChangeBadge } from '../components/sr/SrChangeBadge';
import { SrUrgencyBadge } from '../components/sr/SrUrgencyBadge';
import { Spinner } from '../components/Spinner';
import { isReceptionQueueRole, isCrossQueueSearchRole, isPersonalQueueTabRole } from '../utils/queueRole';
import { getQueueBucket, hasQueueSearchFilters } from '../utils/queueBucket';
import {
  canCompleteMiscIcpWithoutIndici,
  getMarkCompleteConfirmCopy,
  SR_COMPLETION_OUTBOUND_WARNING,
} from '../utils/referralCompletion';

export const ReferralQueuePage: React.FC = () => {
  // Use backend user info for role (reflects database role, not token claims)
  const { user: currentUser } = useCurrentUser();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'ready' | 'missing' | 'assigned' | 'unassigned' | 'escalated' | 'miscellaneous' | 'completed' | 'awaiting_reply' | 'ae_queue'>('assigned');
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [pagination, setPagination] = useState<PaginatedReferrals['pagination'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingReferralId, setLoadingReferralId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [referralToComplete, setReferralToComplete] = useState<Referral | null>(null);
  const [completing, setCompleting] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [completionWarning, setCompletionWarning] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [showClarificationModal, setShowClarificationModal] = useState(false);
  const [referralToClarify, setReferralToClarify] = useState<Referral | null>(null);
  
  // Delete state (AXIS145)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [referralToDelete, setReferralToDelete] = useState<Referral | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null);
  const [tabCounts, setTabCounts] = useState<{
    ready: number;
    missing: number;
    escalated: number;
    assigned: number;
    unassigned: number;
    miscellaneous: number;
    completed: number;
    awaiting_reply: number;
    ae_queue: number;
  }>({
    ready: 0,
    missing: 0,
    escalated: 0,
    assigned: 0,
    unassigned: 0,
    miscellaneous: 0,
    completed: 0,
    awaiting_reply: 0,
    ae_queue: 0,
  });
  const [tabCountsLoading, setTabCountsLoading] = useState(true);
  const [tabMatchCounts, setTabMatchCounts] = useState<{
    assigned?: number;
    unassigned?: number;
    awaiting_reply?: number;
    escalated?: number;
    miscellaneous?: number;
    completed?: number;
    ae_queue?: number;
    ready?: number;
    missing?: number;
  } | null>(null);
  const [queueView, setQueueView] = useState<'my' | 'all'>('my');

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);

  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [staffUsers, setStaffUsers] = useState<User[]>([]);
  const [userRole, setUserRole] = useState<string>('');
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string | null>(null);
  const [isReceptionistViewingAssignments, setIsReceptionistViewingAssignments] = useState(false);

  // Filter state for display
  const [activeFilters, setActiveFilters] = useState<{
    referralType?: string[];
    funder?: string[];
    bodyPart?: string[];
    providerOrg?: string[];
    imagingFacility?: string[];
    providerName?: string[];
    search?: string;
    assignee?: string; // AXIS148: Added assignee filter
    triageCategory?: number[]; // AXIS164: Triage category filter (1-4)
    clinicNames?: string[]; // AXISREF399: Recommended clinic filter (enum-driven names)
  }>({});

  const isReceptionQueue = isReceptionQueueRole(userRole);
  const isCrossQueueSearch = isCrossQueueSearchRole(userRole);
  const isAllReferralsView = isCrossQueueSearch && queueView === 'all';
  const filtersActive = hasQueueSearchFilters(activeFilters);

  // AXIS148: URL persistence for assignee filter
  // AXIS229: URL persistence for currentPage
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedReferralId = searchParams.get('referral') || searchParams.get('referralId');
  const deepLinkOpenedRef = useRef(false);
  const lastUrlSearchRef = useRef<string | null>(null);

  const syncReferralIdInUrl = useCallback((referralId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (referralId) {
      next.set('referral', referralId);
      next.delete('referralId');
    } else {
      next.delete('referral');
      next.delete('referralId');
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openFromDeepLink = useCallback(async (referralId: string) => {
    try {
      const referral = await ApiService.getReferralById(referralId);
      setSelectedReferral(referral);
      setShowDetailModal(true);
    } catch {
      syncReferralIdInUrl(null);
    }
  }, [syncReferralIdInUrl]);

  useEffect(() => {
    if (!highlightedReferralId || deepLinkOpenedRef.current) {
      return;
    }
    deepLinkOpenedRef.current = true;
    openFromDeepLink(highlightedReferralId);
  }, [highlightedReferralId, openFromDeepLink]);

  // AXIS148: Initialize assignee filter from URL on mount
  // AXIS164: Initialize triage_category filter from URL on mount
  // AXIS229: Initialize currentPage from URL on mount
  useEffect(() => {
    const assigneeFromUrl = searchParams.get('assignee');
    const triageCategoryFromUrl = searchParams.get('triage_category');
    const clinicNameFromUrl = searchParams.get('clinic_name'); // AXISREF399
    const pageFromUrl = searchParams.get('page');
    const updates: Partial<typeof activeFilters> = {};
    if (assigneeFromUrl && userRole === 'admin') {
      updates.assignee = assigneeFromUrl;
    }
    if (triageCategoryFromUrl && triageCategoryFromUrl.trim()) {
      const parsed = triageCategoryFromUrl.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !Number.isNaN(n) && n >= 1 && n <= 4);
      if (parsed.length > 0) {
        updates.triageCategory = [...new Set(parsed)];
      }
    }
    // AXISREF399: Restore clinic filter from URL (string names, same pattern as other filters)
    if (clinicNameFromUrl && clinicNameFromUrl.trim()) {
      const names = clinicNameFromUrl.split(',').map(s => s.trim()).filter(Boolean);
      if (names.length > 0) {
        updates.clinicNames = [...new Set(names)];
      }
    }
    if (Object.keys(updates).length > 0) {
      setActiveFilters(prev => ({ ...prev, ...updates }));
    }
    // AXIS229: Restore page from URL
    if (pageFromUrl) {
      const pageNum = parseInt(pageFromUrl, 10);
      if (!isNaN(pageNum) && pageNum > 0) {
        setCurrentPage(pageNum);
      }
    }
  }, [userRole]); // Only run when userRole is set

  // AXISREF350: React to URL view/search changes (e.g. nav quick search handoff, browser navigation)
  useEffect(() => {
    if (!isCrossQueueSearch) {
      return;
    }

    const viewFromUrl = searchParams.get('view');
    const searchFromUrl = searchParams.get('search')?.trim() ?? '';

    if (searchFromUrl) {
      setQueueView('all');
      if (lastUrlSearchRef.current !== searchFromUrl) {
        lastUrlSearchRef.current = searchFromUrl;
        setActiveFilters((prev) => ({ ...prev, search: searchFromUrl }));
        setCurrentPage(1);
      }
      return;
    }

    lastUrlSearchRef.current = null;

    if (viewFromUrl === 'all') {
      setQueueView('all');
    }
  }, [searchParams, isCrossQueueSearch]);

  // AXIS148: Update URL when assignee filter changes
  // AXIS164: Update URL when triage category filter changes
  // AXIS229: Update URL when currentPage changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (userRole === 'admin' && activeTab === 'assigned') {
      if (activeFilters.assignee) {
        newSearchParams.set('assignee', activeFilters.assignee);
      } else {
        newSearchParams.delete('assignee');
      }
    }
    if (activeFilters.triageCategory && activeFilters.triageCategory.length > 0) {
      newSearchParams.set('triage_category', activeFilters.triageCategory.join(','));
    } else {
      newSearchParams.delete('triage_category');
    }
    // AXISREF399: Persist clinic filter to URL
    if (activeFilters.clinicNames && activeFilters.clinicNames.length > 0) {
      newSearchParams.set('clinic_name', activeFilters.clinicNames.join(','));
    } else {
      newSearchParams.delete('clinic_name');
    }
    // AXIS229: Persist currentPage to URL
    if (currentPage > 1) {
      newSearchParams.set('page', currentPage.toString());
    } else {
      newSearchParams.delete('page');
    }
    if (isCrossQueueSearch) {
      if (queueView === 'all') {
        newSearchParams.set('view', 'all');
        if (activeFilters.search?.trim()) {
          newSearchParams.set('search', activeFilters.search.trim());
        } else {
          newSearchParams.delete('search');
        }
      } else {
        newSearchParams.delete('view');
        newSearchParams.delete('search');
      }
    }
    if (newSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [activeFilters.assignee, activeFilters.triageCategory, activeFilters.clinicNames, activeFilters.search, currentPage, userRole, activeTab, queueView, isCrossQueueSearch, searchParams, setSearchParams]);

  // Sort state (separate from filters) — AXISREF352: server-side sort
  const [sortBy, setSortBy] = useState<'receivedDate' | 'name'>('receivedDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const sortLocked = isAllReferralsView && filtersActive;

  const getEffectiveSort = (): { sort: 'received_at' | 'patient_name'; sortDirection: 'asc' | 'desc' } => {
    if (sortLocked) {
      return { sort: 'received_at', sortDirection: 'desc' };
    }
    return {
      sort: sortBy === 'name' ? 'patient_name' : 'received_at',
      sortDirection: sortOrder,
    };
  };

  // Handle column header click for sorting
  const handleSort = (column: 'receivedDate' | 'name') => {
    if (sortLocked) {
      return;
    }
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Render sortable column header
  const renderSortableHeader = (label: string, column: 'receivedDate' | 'name') => {
    if (sortLocked) {
      return <th style={{ fontWeight: 'normal' }}>{label}</th>;
    }

    const isActive = sortBy === column;

    return (
      <th
        onClick={() => handleSort(column)}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          fontWeight: isActive ? 'bold' : 'normal'
        }}
        title={`Sort by ${label}`}
      >
        <Flex align="center" gap={4}>
          {label}
          <FeatherIcon
            icon={isActive ? (sortOrder === 'asc' ? "chevron-up" : "chevron-down") : "chevron-down"}
            size={16}
            style={{ opacity: isActive ? 1 : 0.2 }}
          />
        </Flex>
      </th>
    );
  };

  // Available filter options from database
  const [filterOptions, setFilterOptions] = useState<{
    referralTypes: string[];
    funders: string[];
    bodyParts: string[];
    providerOrgs: string[];
    imagingFacilities: string[];
    providerNames: string[];
    clinicNames?: string[]; // AXISREF399: Clinic enum options from filter_options
  }>({
    referralTypes: [],
    funders: [],
    bodyParts: [],
    providerOrgs: [],
    imagingFacilities: [],
    providerNames: [],
  });

  // Ref for dropdown container
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Ref to track if user info has been loaded (prevents double execution in StrictMode)
  const userInfoLoadedRef = useRef(false);
  // AbortControllers to cancel in-flight requests on unmount or StrictMode cleanup
  const fetchTabCountsAbortRef = useRef<AbortController | null>(null);
  const fetchReferralsAbortRef = useRef<AbortController | null>(null);

  // Cleanup: cancel in-flight requests when component unmounts
  useEffect(() => {
    return () => {
      if (fetchTabCountsAbortRef.current) {
        fetchTabCountsAbortRef.current.abort();
      }
      if (fetchReferralsAbortRef.current) {
        fetchReferralsAbortRef.current.abort();
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Map tab names to status filters
  const getStatusFilter = (tab: string) => {
    switch (tab) {
      case 'ready':
        return 'ready_for_indici';
      case 'missing':
        return 'waiting_on_info';
      case 'escalated':
        return 'escalated'; // Filter by escalated status
      case 'assigned':
        return 'assigned'; // Filter by assigned status
      case 'unassigned':
        return 'unassigned'; // Filter by unassigned status
      case 'miscellaneous':
        return 'miscellaneous'; // Special filter for miscellaneous referrals (international, ICP, specific addressees)
      case 'completed':
        return 'completed'; // Filter by completed status
      case 'awaiting_reply':
        return 'awaiting_reply'; // Filter by awaiting reply status
      case 'ae_queue':
        return null; // AXIS076: Special handling
      default:
        return undefined;
    }
  };

  // Calculate column count based on active tab
  const getColumnCount = () => {
    const queueColumn = isAllReferralsView ? 1 : 0;
    if (activeTab === 'awaiting_reply' && !isAllReferralsView) {
      return 9;
    }
    return 8 + queueColumn;
  };

  // Load user info and staff
  useEffect(() => {
    const loadUserInfo = async () => {
      // Only proceed if we have currentUser data and haven't loaded yet
      if (!currentUser || userInfoLoadedRef.current) {
        return;
      }
      userInfoLoadedRef.current = true;

      try {
        // Use backend user role from useCurrentUser hook (reflects database role)
        const role = currentUser.role || '';
        setUserRole(role);
        setCurrentUserId(currentUser.id || '');
        setIsReceptionistViewingAssignments(isReceptionQueueRole(role));

        const users = await UserService.getUsers();
        setStaffUsers(users as any);
      } catch (err) {
        console.error('Error loading user info:', err);
      }
    };
    loadUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Set default tab based on user role
  useEffect(() => {
    if (isReceptionQueueRole(userRole)) {
      setActiveTab('assigned');
    } else if (userRole === 'admin') {
      setActiveTab('assigned');
    } else if (userRole) {
      setActiveTab('ready');
    }
  }, [userRole]);

  // Fetch summary data (filter options) from summary endpoint
  const fetchSummary = async () => {
    try {
      const summary = await ApiService.getReferralsSummary();
      
      // Set filter options
      setFilterOptions({
        referralTypes: summary.filter_options.referral_types,
        funders: summary.filter_options.funders,
        bodyParts: summary.filter_options.body_parts,
        providerOrgs: summary.filter_options.provider_orgs,
        imagingFacilities: summary.filter_options.imaging_facilities,
        providerNames: summary.filter_options.provider_names,
        clinicNames: summary.filter_options.clinic_names, // AXISREF399: Clinic enum options
      });
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  // Fetch tab counts using getReferrals with pagination (like AI triage page)
  // This ensures counts match exactly what the backend query returns
  const fetchTabCounts = async () => {
    try {
      // Cancel any previous in-flight requests
      if (fetchTabCountsAbortRef.current) {
        fetchTabCountsAbortRef.current.abort();
      }
      fetchTabCountsAbortRef.current = new AbortController();
      const signal = fetchTabCountsAbortRef.current.signal;

      // Determine assignee filter based on role
      const assigneeFilter = userRole === 'admin' ? undefined : currentUserId;

      // Fetch counts for each tab using getReferrals with pageSize 1
      // The total_count from pagination gives us the accurate count
      const emptyResult = {
        referrals: [],
        pagination: {
          current_page: 1,
          page_size: 1,
          total_count: 0,
          total_pages: 0,
          has_next: false,
          has_previous: false,
        },
      };

      const receptionQueue = isReceptionQueueRole(userRole);

      const [readyData, missingData, escalatedData, assignedData, unassignedData, miscellaneousData, completedData, awaitingReplyData, aeQueueData] = await Promise.all([
        ApiService.getReferrals(1, 1, 'ready_for_indici', undefined, assigneeFilter, undefined, undefined, undefined, signal),
        ApiService.getReferrals(1, 1, 'waiting_on_info', undefined, assigneeFilter, undefined, undefined, undefined, signal),
        userRole === 'admin' ? ApiService.getReferrals(1, 1, 'escalated', undefined, undefined, undefined, undefined, undefined, signal) : Promise.resolve(emptyResult),
        (userRole === 'admin' || receptionQueue)
          ? ApiService.getReferrals(
              1,
              1,
              'assigned',
              undefined,
              receptionQueue ? currentUserId : undefined,
              undefined,
              undefined,
              undefined,
              signal,
            )
          : Promise.resolve(emptyResult),
        (userRole === 'admin' || receptionQueue)
          ? ApiService.getReferrals(1, 1, 'unassigned', undefined, undefined, undefined, undefined, undefined, signal)
          : Promise.resolve(emptyResult),
        userRole === 'admin' ? ApiService.getReferrals(1, 1, 'miscellaneous', undefined, undefined, undefined, undefined, undefined, signal) : Promise.resolve(emptyResult),
        userRole === 'admin' ? ApiService.getReferrals(1, 1, 'completed', undefined, undefined, undefined, undefined, undefined, signal) : Promise.resolve(emptyResult),
        (userRole === 'admin' || receptionQueue) ? ApiService.getReferrals(1, 1, 'awaiting_reply', undefined, undefined, undefined, undefined, undefined, signal) : Promise.resolve(emptyResult),
        (userRole === 'admin' || receptionQueue) ? ApiService.getReferrals(1, 1, undefined, undefined, undefined, undefined, undefined, true, signal) : Promise.resolve(emptyResult),
      ]);

      setTabCounts({
        ready: readyData.pagination.total_count,
        missing: missingData.pagination.total_count,
        escalated: escalatedData.pagination.total_count,
        assigned: assignedData.pagination.total_count,
        unassigned: unassignedData.pagination.total_count,
        miscellaneous: miscellaneousData.pagination.total_count,
        completed: completedData.pagination.total_count,
        awaiting_reply: awaitingReplyData.pagination.total_count,
        ae_queue: aeQueueData.pagination.total_count,
      });
      setTabCountsLoading(false);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Request was cancelled, don't update state
      }
      console.error('Error fetching tab counts:', err);
      setTabCounts({ ready: 0, missing: 0, escalated: 0, assigned: 0, unassigned: 0, miscellaneous: 0, completed: 0, awaiting_reply: 0, ae_queue: 0 });
      setTabCountsLoading(false);
    }
  };

  // Load summary data (filter options) on mount and when user changes
  useEffect(() => {
    if (currentUserId) {
      fetchSummary();
      fetchTabCounts();
    }
  }, [currentUserId, userRole]);

  // Fetch per-tab match counts when searching in My referrals view (AXISREF350)
  const fetchTabMatchCounts = async () => {
    if (!isCrossQueueSearch || queueView !== 'my' || !filtersActive) {
      setTabMatchCounts(null);
      return;
    }

    try {
      const signal = fetchTabCountsAbortRef.current?.signal;
      const search = activeFilters.search || undefined;
      const filterArgs = {
        triageCategory: activeFilters.triageCategory,
        referralType: activeFilters.referralType,
        funder: activeFilters.funder,
        bodyPart: activeFilters.bodyPart,
        providerOrg: activeFilters.providerOrg,
        imagingFacility: activeFilters.imagingFacility,
        providerName: activeFilters.providerName,
      };

      const fetchCount = (status?: string, assignee?: string, aeQueue?: boolean) =>
        ApiService.getReferrals(
          1,
          1,
          status,
          search,
          assignee,
          undefined,
          filterArgs.triageCategory,
          aeQueue || undefined,
          signal,
          filterArgs.referralType,
          filterArgs.funder,
          filterArgs.bodyPart,
          filterArgs.providerOrg,
          filterArgs.imagingFacility,
          filterArgs.providerName,
        );

      if (isReceptionQueue) {
        const [assignedData, unassignedData, awaitingReplyData] = await Promise.all([
          fetchCount('assigned', currentUserId),
          fetchCount('unassigned'),
          fetchCount('awaiting_reply'),
        ]);
        setTabMatchCounts({
          assigned: assignedData.pagination.total_count,
          unassigned: unassignedData.pagination.total_count,
          awaiting_reply: awaitingReplyData.pagination.total_count,
        });
        return;
      }

      if (userRole === 'admin') {
        const [
          assignedData,
          unassignedData,
          awaitingReplyData,
          escalatedData,
          miscellaneousData,
          completedData,
          aeQueueData,
        ] = await Promise.all([
          fetchCount('assigned', activeFilters.assignee),
          fetchCount('unassigned'),
          fetchCount('awaiting_reply'),
          fetchCount('escalated'),
          fetchCount('miscellaneous'),
          fetchCount('completed'),
          fetchCount(undefined, undefined, true),
        ]);
        setTabMatchCounts({
          assigned: assignedData.pagination.total_count,
          unassigned: unassignedData.pagination.total_count,
          awaiting_reply: awaitingReplyData.pagination.total_count,
          escalated: escalatedData.pagination.total_count,
          miscellaneous: miscellaneousData.pagination.total_count,
          completed: completedData.pagination.total_count,
          ae_queue: aeQueueData.pagination.total_count,
        });
        return;
      }

      if (isPersonalQueueTabRole(userRole)) {
        const [readyData, missingData] = await Promise.all([
          fetchCount('ready_for_indici', currentUserId),
          fetchCount('waiting_on_info', currentUserId),
        ]);
        setTabMatchCounts({
          ready: readyData.pagination.total_count,
          missing: missingData.pagination.total_count,
        });
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setTabMatchCounts(null);
    }
  };

  // Fetch referrals from API
  const fetchReferrals = async () => {
    try {
      // Cancel any previous in-flight requests
      if (fetchReferralsAbortRef.current) {
        fetchReferralsAbortRef.current.abort();
      }
      fetchReferralsAbortRef.current = new AbortController();
      const signal = fetchReferralsAbortRef.current.signal;

      setLoading(true);
      setError(null);

      if (isAllReferralsView && !filtersActive) {
        setReferrals([]);
        setPagination({
          current_page: 1,
          page_size: pageSize,
          total_count: 0,
          total_pages: 0,
          has_next: false,
          has_previous: false,
        });
        setLoading(false);
        return;
      }

      // Get status filter based on active tab and role
      // Backend now handles priority-based filtering, so always send the tab name
      // Exception: Receptionists viewing "Assigned to Me" should use 'assigned' status
      let statusFilter: string | undefined = undefined;
      let aeeQueueFilter = false; // AXIS076
      if (isAllReferralsView) {
        statusFilter = undefined;
      } else if (activeTab === 'ae_queue') {
        // AXIS235: AE queue uses special filter
        aeeQueueFilter = true;
      } else if (isReceptionQueue && isReceptionistViewingAssignments) {
        statusFilter = 'assigned'; // Reception/PA viewing their assignments
      } else if (isReceptionQueue && activeTab === 'awaiting_reply') {
        statusFilter = 'awaiting_reply';
      } else {
        const filterResult = getStatusFilter(activeTab);
        statusFilter = filterResult === null ? undefined : filterResult;
      }

      // Determine assignee filter based on role and view
      let assigneeFilter: string | undefined;
      if (isAllReferralsView) {
        assigneeFilter = undefined;
      } else if (userRole === 'admin') {
        // AXIS148: Admin can filter by assignee using the filter panel
        assigneeFilter = activeFilters.assignee || undefined; // Admin uses filter panel assignee, or sees all
      } else if (isReceptionQueue && isReceptionistViewingAssignments) {
        assigneeFilter = currentUserId; // Reception/PA sees their assignments
      } else if (isReceptionQueue && !isReceptionistViewingAssignments) {
        assigneeFilter = undefined; // Reception/PA sees all unassigned
      } else {
        assigneeFilter = currentUserId; // Default: other users see their assignments
      }


      // AXIS229: Pass search filter to backend instead of applying client-side
      // AXIS242: Pass all filter parameters to backend for server-side filtering
      const { sort, sortDirection } = getEffectiveSort();
      const data = await ApiService.getReferrals(
        currentPage,
        pageSize,
        statusFilter,
        activeFilters.search || undefined,
        assigneeFilter,
        undefined,
        activeFilters.triageCategory,
        aeeQueueFilter || undefined,
        signal,
        // AXIS242: Additional server-side filter parameters
        activeFilters.referralType,
        activeFilters.funder,
        activeFilters.bodyPart,
        activeFilters.providerOrg,
        activeFilters.imagingFacility,
        activeFilters.providerName,
        isAllReferralsView ? 'reception' : undefined,
        undefined,
        // AXISREF399: Recommended clinic filter (enum-driven names, same pattern as other filters)
        activeFilters.clinicNames && activeFilters.clinicNames.length > 0 ? activeFilters.clinicNames : undefined,
        sort,
        sortDirection,
      );

      setReferrals(data.referrals);
      setPagination(data.pagination);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Request was cancelled, don't update state
      }
      console.error('Error fetching referrals:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch referrals');
      setReferrals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
  }, [currentPage, pageSize, activeTab, currentUserId, isReceptionistViewingAssignments, activeFilters, sortBy, sortOrder, queueView, isAllReferralsView]);

  useEffect(() => {
    fetchTabMatchCounts();
  }, [activeFilters, queueView, isCrossQueueSearch, isReceptionQueue, userRole, currentUserId, filtersActive]);

  // Note: Tab counts are now fetched via fetchTabCounts() which uses getReferrals with pagination
  // This ensures counts match exactly what the backend query returns (like AI triage page)
  // Tab counts are refreshed when currentUserId or userRole changes, and after referral updates

  // Memoize the filters change handler to prevent unnecessary re-renders
  const handleFiltersChange = useCallback((filters: FilterConfig) => {
    const newFilters = {
      search: filters.search || '',
      referralType: filters.referralType || [],
      funder: filters.funder || [],
      bodyPart: filters.bodyPart || [],
      providerOrg: filters.providerOrg || [],
      imagingFacility: filters.imagingFacility || [],
      providerName: filters.providerName || [],
      assignee: filters.assignee || '', // AXIS148: Added assignee filter
      triageCategory: filters.triageCategory ?? [], // AXIS164: Triage category filter
      clinicNames: filters.clinicNames ?? [], // AXISREF399: Clinic filter (enum-driven names)
    };

    // Update filters state - this will trigger useEffect to refetch referrals
    setActiveFilters(newFilters);
    // AXIS229: Reset pagination to page 1 when filters change (URL updated via useEffect)
    setCurrentPage(1);
  }, []);

  const handleFollowUp = (referral: Referral) => {
    setSelectedReferral(referral);
    setShowFollowUp(true);
  };

  const handleViewDetail = async (referralId: string) => {
    try {
      const cachedReferral = referrals.find(r => r.id === referralId);

      if (cachedReferral) {
        setSelectedReferral(cachedReferral);
        setShowDetailModal(true);
        syncReferralIdInUrl(referralId);
        
        (async () => {
          try {
            const fullReferral = await ApiService.getReferralById(referralId);
            setSelectedReferral(prev => {
              if (prev?.id === referralId) {
                return fullReferral;
              }
              return prev;
            });
          } catch (err) {
            console.error('Error fetching full referral details:', err);
          }
        })();
      } else {
        setLoadingReferralId(referralId);
        const referral = await ApiService.getReferralById(referralId);
        setSelectedReferral(referral);
        setShowDetailModal(true);
        syncReferralIdInUrl(referralId);
        setLoadingReferralId(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch referral details');
      console.error('Error fetching referral details:', err);
      setLoadingReferralId(null);
    }
  };

  const handleReferralUpdate = (updatedReferral: Referral) => {
    setSelectedReferral(updatedReferral);
    setReferrals(prevReferrals =>
      prevReferrals.map(r => r.id === updatedReferral.id ? updatedReferral : r)
    );
    fetchTabCounts();
  };

  // Field name mapping for user-friendly display
  const getFieldDisplayLabel = (fieldName: string): string => {
    const fieldLabels: Record<string, string> = {
      'patient_first_name': 'First Name',
      'patient_last_name': 'Last Name',
      'patient_dob': 'Date of Birth',
      'nhi_number': 'NHI Number',
      'contact_method': 'Contact Method (phone or email)',
      'contact_phone': 'Contact Phone',
      'contact_email': 'Contact Email',
      'clinical_notes': 'Clinical Notes',
      'referral_type': 'Referral Type',
      'body_side': 'Body Side',
      'body_part': 'Body Part',
      'acc_claim_number': 'ACC Claim Number',
      'acc_number_validation': 'The ACC Claim Number needs to be successfully validated.',
      'referring_provider_name': 'Provider Name',
      'referring_provider_organization': 'Provider Organisation',
      'referring_provider_contact': 'Provider Contact (email or EDI)',
      'referring_provider_email': 'Provider Email',
      'referring_provider_edi': 'Provider EDI',
    };
    return fieldLabels[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleMarkComplete = (referral: Referral) => {
    setReferralToComplete(referral);
    setShowCompleteConfirm(true);
    setCompletionError(null);
    setCompletionWarning(null);
    setMissingFields([]);
  };

  const handleRequestClarification = (referral: Referral) => {
    setReferralToClarify(referral);
    setShowClarificationModal(true);
  };

  const confirmMarkComplete = async (srResponse?: SRCompleteResponsePayload) => {
    if (!referralToComplete) return;

    setCompleting(true);
    setCompletionError(null);
    setCompletionWarning(null);
    setMissingFields([]);

    try {
      // AXISREF437: complete ICP miscellaneous referrals may complete directly from the
      // Miscellaneous queue without going through AI Triage; this path never sends the
      // referral to Indici, and the confirmation dialog above states this explicitly.
      const miscNoIndiciConfirm = canCompleteMiscIcpWithoutIndici(referralToComplete);
      const result = await ApiService.updateReferralStatus(
        referralToComplete.id,
        'completed',
        isSrReferral(referralToComplete) && srResponse
          ? { srResponse }
          : miscNoIndiciConfirm
            ? { miscNoIndiciConfirm: true }
            : undefined,
      );
      if (result.success === false) {
        throw new Error(
          result.error ||
            result.message ||
            'The referral could not be marked as complete. Please try again.',
        );
      }
      await Promise.all([fetchReferrals(), fetchTabCounts()]);
      setShowCompleteConfirm(false);
      setReferralToComplete(null);

      if (result.sr_message?.attempted && result.sr_message.sent === false) {
        setCompletionWarning(SR_COMPLETION_OUTBOUND_WARNING);
      }
    } catch (err) {
      // Try to extract missing fields from error response
      let errorMessage = 'Failed to mark referral as completed. Please try again.';
      let extractedMissingFields: string[] = [];
      
      if (err instanceof Error) {
        errorMessage = err.message;
        
        // Check if error object has missing_fields property (from API response)
        if (err && typeof err === 'object') {
          const errObj = err as any;
          if (errObj.missing_fields && Array.isArray(errObj.missing_fields)) {
            extractedMissingFields = errObj.missing_fields;
          }
        }
      }
      
      setCompletionError(errorMessage);
      setMissingFields(extractedMissingFields);
    } finally {
      setCompleting(false);
    }
  };

  // Bulk selection handlers
  const handleSelectReferral = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === referrals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(referrals.map(r => r.id)));
    }
  };

  const handleBulkAssign = async (userId: string) => {
    try {
      setBulkAssignLoading(true);
      const token = await AuthService.getAccessToken();

      // Assign all selected referrals
      await Promise.all(
        Array.from(selectedIds).map(id =>
          ApiService.assignReferral(id, userId, token)
        )
      );

      // Refresh and close
      await Promise.all([fetchReferrals(), fetchTabCounts()]);
      setSelectedIds(new Set());
      setShowBulkAssignModal(false);
      setSearchTerm('');
      setShowDropdown(false);
      setSelectedAssigneeId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign referrals');
    } finally {
      setBulkAssignLoading(false);
    }
  };

  // Delete handlers (AXIS145)
  const handleDelete = (referral: Referral) => {
    setReferralToDelete(referral);
    setShowDeleteConfirm(true);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!referralToDelete) return;
    
    setDeleting(true);
    setDeleteError(null);
    
    try {
      await ApiService.deleteReferral(referralToDelete.id);
      await Promise.all([fetchReferrals(), fetchTabCounts()]);
      setShowDeleteConfirm(false);
      setReferralToDelete(null);
      // Show success message (could use toast notification if available)
      setError(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete referral');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setShowBulkDeleteConfirm(true);
    setBulkDeleteError(null);
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    setBulkDeleting(true);
    setBulkDeleteError(null);
    
    try {
      const referralIds = Array.from(selectedIds);
      const result = await ApiService.bulkDeleteReferrals(referralIds);
      
      // Refresh and close
      await Promise.all([fetchReferrals(), fetchTabCounts()]);
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
      
      // Show success/error message
      if (result.failed_count > 0) {
        setBulkDeleteError(`${result.deleted_count} deleted, ${result.failed_count} failed`);
      } else {
        setError(null);
      }
    } catch (err) {
      setBulkDeleteError(err instanceof Error ? err.message : 'Failed to delete referrals');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleQueueViewChange = (view: 'my' | 'all') => {
    setQueueView(view);
    setCurrentPage(1);
    if (view === 'my') {
      setTabMatchCounts(null);
    }
  };

  return (
    <div className="page-container">
      <div className="referral-queue" data-testid="referral-queue" style={{ display: 'flex', gap: '20px' }}>

        {/* LEFT SIDEBAR - FILTERS */}
        <div style={{
          width: '280px',
          flexShrink: 0,
          borderRight: '1px solid #ddd',
          paddingRight: '20px',
        }}>
          <ReferralFilterPanel
            onFiltersChange={handleFiltersChange}
            isLoading={loading}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            availableOptions={filterOptions}
            // AXIS148: Props for assignee filter
            staffUsers={staffUsers}
            currentUserId={currentUserId}
            userRole={userRole}
            activeTab={activeTab}
            appliedTriageCategory={activeFilters.triageCategory}
            appliedClinicNames={activeFilters.clinicNames}
            showQueueViewToggle={isCrossQueueSearch}
            queueView={queueView}
            onQueueViewChange={handleQueueViewChange}
            syncedSearch={activeFilters.search}
          />
        </div>

        {/* RIGHT MAIN CONTENT */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {completionWarning && (
            <div
              style={{
                marginBottom: '12px',
                padding: '10px 12px',
                backgroundColor: '#fff3e0',
                border: '1px solid #ffb74d',
                borderRadius: '4px',
                color: '#e65100',
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                alignItems: 'flex-start',
              }}
            >
              <span>{completionWarning}</span>
              <Button variant="icon" onClick={() => setCompletionWarning(null)} title="Dismiss">
                <FeatherIcon icon="x" size={16} />
              </Button>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>Referral Queue</h3>
            <div className="queue-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {/* AXIS148: Quick filter button for admin users on Assigned tab */}
              {userRole === 'admin' && activeTab === 'assigned' && (
                <Button
                  variant={activeFilters.assignee === currentUserId ? 'primary' : 'secondary'}
                  onClick={() => {
                    if (activeFilters.assignee === currentUserId) {
                      // Clear the filter
                      handleFiltersChange({
                        ...activeFilters,
                        assignee: undefined,
                      });
                    } else {
                      // Set filter to current user
                      handleFiltersChange({
                        ...activeFilters,
                        assignee: currentUserId,
                      });
                    }
                  }}
                  disabled={loading}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                  }}
                >
                  <FeatherIcon icon="user" size={16} />
                  {activeFilters.assignee === currentUserId ? 'Showing My Assignments' : 'Show My Assignments'}
                </Button>
              )}
              {selectedIds.size > 0 && !(isReceptionQueue && isReceptionistViewingAssignments) && (
                <>
                  {userRole === 'admin' && (
                    <Button
                      variant="danger"
                      onClick={handleBulkDelete}
                      disabled={bulkDeleting}
                      isLoading={bulkDeleting}
                    >
                      Delete {selectedIds.size} Selected
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={() => {
                      // For receptionist in unassigned view, directly assign to self
                      if (isReceptionQueue && !isReceptionistViewingAssignments) {
                        handleBulkAssign(currentUserId);
                      } else {
                        // For others, show modal to choose assignee
                        setShowBulkAssignModal(true);
                      }
                    }}
                    disabled={bulkAssignLoading}
                    isLoading={bulkAssignLoading}
                  >
                    {isReceptionQueue && !isReceptionistViewingAssignments
                      ? 'Assign to Me'
                      : `Assign ${selectedIds.size} Selected`
                    }
                  </Button>
                </>
              )}
            </div>
          </div>

          {!isAllReferralsView && (
          <TabMenu variant="queue">
            {isReceptionQueue && (
              <>
                <TabMenuItem
                  id="assigned"
                  label="Assigned to Me"
                  count={tabCountsLoading ? undefined : tabCounts.assigned}
                  isActive={isReceptionistViewingAssignments && activeTab !== 'awaiting_reply'}
                  hasSearchMatches={(tabMatchCounts?.assigned ?? 0) > 0 && !(isReceptionistViewingAssignments && activeTab === 'assigned')}
                  onClick={() => {
                    setIsReceptionistViewingAssignments(true);
                    setActiveTab('assigned');
                    setCurrentPage(1); // AXIS229: Reset pagination when tab changes (URL updated via useEffect)
                  }}
                />
                <TabMenuItem
                  id="unassigned"
                  label="Unassigned"
                  count={tabCountsLoading ? undefined : tabCounts.unassigned}
                  isActive={!isReceptionistViewingAssignments && activeTab !== 'awaiting_reply'}
                  hasSearchMatches={(tabMatchCounts?.unassigned ?? 0) > 0 && activeTab !== 'unassigned'}
                  onClick={() => {
                    setIsReceptionistViewingAssignments(false);
                    setActiveTab('unassigned');
                    setCurrentPage(1);
                  }}
                />
                <TabMenuItem
                  id="awaiting_reply"
                  label="Awaiting Reply"
                  count={tabCountsLoading ? undefined : tabCounts.awaiting_reply}
                  isActive={activeTab === 'awaiting_reply'}
                  hasSearchMatches={(tabMatchCounts?.awaiting_reply ?? 0) > 0 && activeTab !== 'awaiting_reply'}
                  onClick={() => {
                    setActiveTab('awaiting_reply');
                    setIsReceptionistViewingAssignments(false);
                    setCurrentPage(1);
                  }}
                />
              </>
            )}

            {!isReceptionQueue && (
              <>
                {userRole === 'admin' ? (
                  <>
                    <TabMenuItem
                      id="assigned"
                      label="Assigned"
                      count={tabCountsLoading ? undefined : tabCounts.assigned}
                      isActive={activeTab === 'assigned'}
                      hasSearchMatches={!!tabMatchCounts?.assigned && tabMatchCounts.assigned > 0 && activeTab !== 'assigned'}
                      onClick={() => { setActiveTab('assigned'); setCurrentPage(1); }}
                    />
                    <TabMenuItem
                      id="unassigned"
                      label="Unassigned"
                      count={tabCountsLoading ? undefined : tabCounts.unassigned}
                      isActive={activeTab === 'unassigned'}
                      hasSearchMatches={!!tabMatchCounts?.unassigned && tabMatchCounts.unassigned > 0 && activeTab !== 'unassigned'}
                      onClick={() => { setActiveTab('unassigned'); setCurrentPage(1); }}
                    />
                    <TabMenuItem
                      id="awaiting_reply"
                      label="Awaiting Reply"
                      count={tabCountsLoading ? undefined : tabCounts.awaiting_reply}
                      isActive={activeTab === 'awaiting_reply'}
                      hasSearchMatches={!!tabMatchCounts?.awaiting_reply && tabMatchCounts.awaiting_reply > 0 && activeTab !== 'awaiting_reply'}
                      onClick={() => { setActiveTab('awaiting_reply'); setCurrentPage(1); }}
                    />
                    <TabMenuItem
                      id="escalated"
                      label="Escalated"
                      count={tabCountsLoading ? undefined : tabCounts.escalated}
                      isActive={activeTab === 'escalated'}
                      hasSearchMatches={!!tabMatchCounts?.escalated && tabMatchCounts.escalated > 0 && activeTab !== 'escalated'}
                      onClick={() => { setActiveTab('escalated'); setCurrentPage(1); }}
                    />
                    <TabMenuItem
                      id="miscellaneous"
                      label="Miscellaneous"
                      count={tabCountsLoading ? undefined : tabCounts.miscellaneous}
                      isActive={activeTab === 'miscellaneous'}
                      hasSearchMatches={!!tabMatchCounts?.miscellaneous && tabMatchCounts.miscellaneous > 0 && activeTab !== 'miscellaneous'}
                      onClick={() => { setActiveTab('miscellaneous'); setCurrentPage(1); }}
                    />
                    <TabMenuItem
                      id="completed"
                      label="Completed"
                      count={tabCountsLoading ? undefined : tabCounts.completed}
                      isActive={activeTab === 'completed'}
                      hasSearchMatches={!!tabMatchCounts?.completed && tabMatchCounts.completed > 0 && activeTab !== 'completed'}
                      onClick={() => { setActiveTab('completed'); setCurrentPage(1); }}
                    />
                    {/* AXIS235: AE Validation Queue - Admin only */}
                    {userRole === 'admin' && (
                      <TabMenuItem
                        id="ae_queue"
                        label="AE Validation"
                        count={tabCountsLoading ? undefined : tabCounts.ae_queue}
                        isActive={activeTab === 'ae_queue'}
                        hasSearchMatches={!!tabMatchCounts?.ae_queue && tabMatchCounts.ae_queue > 0 && activeTab !== 'ae_queue'}
                        onClick={() => { setActiveTab('ae_queue'); setCurrentPage(1); }}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <TabMenuItem
                      id="ready"
                      label="Ready for Indici"
                      count={tabCountsLoading ? undefined : tabCounts.ready}
                      isActive={activeTab === 'ready'}
                      hasSearchMatches={isPersonalQueueTabRole(userRole) && !!tabMatchCounts?.ready && tabMatchCounts.ready > 0 && activeTab !== 'ready'}
                      onClick={() => { setActiveTab('ready'); setCurrentPage(1); }}
                    />
                    <TabMenuItem
                      id="missing"
                      label="Missing Information"
                      count={tabCountsLoading ? undefined : tabCounts.missing}
                      isActive={activeTab === 'missing'}
                      hasSearchMatches={isPersonalQueueTabRole(userRole) && !!tabMatchCounts?.missing && tabMatchCounts.missing > 0 && activeTab !== 'missing'}
                      onClick={() => { setActiveTab('missing'); setCurrentPage(1); }}
                    />
                  </>
                )}
              </>
            )}
          </TabMenu>
          )}

          {/* AXIS148: Assignee chip (admin users on assigned tab only) */}
          {activeFilters.assignee && userRole === 'admin' && activeTab === 'assigned' ? (
            <FilterChips
              filters={{
                assignee: activeFilters.assignee,
                assigneeDisplayName: activeFilters.assignee === currentUserId
                  ? 'Me'
                  : staffUsers.find(u => u.id === activeFilters.assignee)?.display_name || 'Unknown',
              }}
              onRemoveFilter={(filterKey) => {
                if (filterKey === 'assignee') {
                  handleFiltersChange({
                    ...activeFilters,
                    assignee: undefined,
                  });
                }
              }}
            />
          ) : null}

          <div className="referrals-table">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40px', fontWeight: 'normal' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === referrals.length && referrals.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  {renderSortableHeader('Received', 'receivedDate')}
                  {renderSortableHeader('Patient', 'name')}
                  <th style={{ fontWeight: 'normal' }}>Source</th>
                  {isAllReferralsView && (
                    <th style={{ fontWeight: 'normal' }}>Queue</th>
                  )}
                  {/* AXIS235: Hide Date of Injury for AE queue */}
                  {!isAllReferralsView && activeTab !== 'ae_queue' && activeTab !== 'awaiting_reply' && (
                    <th style={{ fontWeight: 'normal' }}>Date of Injury</th>
                  )}
                  {isAllReferralsView && (
                    <th style={{ fontWeight: 'normal' }}>Date of Injury</th>
                  )}
                  {/* AXIS235: AE-specific columns */}
                  {!isAllReferralsView && activeTab === 'ae_queue' && (
                    <th style={{ fontWeight: 'normal' }}>Days in Queue</th>
                  )}
                  {/* AXIS231: Awaiting Reply-specific columns */}
                  {!isAllReferralsView && activeTab === 'awaiting_reply' && (
                    <>
                      <th style={{ fontWeight: 'normal' }}>Marked At</th>
                      <th style={{ fontWeight: 'normal', minWidth: '200px' }}>Note</th>
                    </>
                  )}
                  <th style={{ fontWeight: 'normal' }}>Assignee</th>
                  <th style={{ fontWeight: 'normal' }}>Status</th>
                  <th style={{ fontWeight: 'normal' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <tr key={`skeleton-${i}`}>
                        <td colSpan={getColumnCount()} style={{ textAlign: 'center', padding: '20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                            <Spinner size="sm" color="secondary" />
                            <span>Loading referrals...</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                ) : error ? (
                  <tr>
                    <td colSpan={getColumnCount()} style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                      Error: {error}
                    </td>
                  </tr>
                ) : referrals.length === 0 ? (
                  <tr>
                    <td colSpan={getColumnCount()} style={{ textAlign: 'center', padding: '20px' }}>
                      {isAllReferralsView && !filtersActive ? (
                        <span>Apply filters to search across all referrals in the queue.</span>
                      ) : isCrossQueueSearch && queueView === 'my' && filtersActive ? (
                        <span>
                          No referrals found in this tab.{' '}
                          <button
                            type="button"
                            onClick={() => handleQueueViewChange('all')}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--axis-vibrant-orange)',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              padding: 0,
                              font: 'inherit',
                            }}
                          >
                            Try searching across all referrals
                          </button>
                        </span>
                      ) : (
                        'No referrals found'
                      )}
                    </td>
                  </tr>
                ) : (
                  referrals.map(referral => (
                    <tr
                      key={referral.id}
                      onClick={() => handleViewDetail(referral.id)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor:
                          highlightedReferralId === referral.id
                            ? 'rgba(37, 99, 235, 0.08)'
                            : undefined,
                      }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(referral.id)}
                          onChange={() => handleSelectReferral(referral.id)}
                        />
                      </td>
                      <td>
                        {referral.received_at ? new Date(referral.received_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td>
                        <span style={{ color: 'var(--axis-vibrant-orange)' }}>
                          {referral.patient_first_name} {referral.patient_last_name}
                        </span>
                        {isSrReferral(referral) && (
                          <>
                            <SrUrgencyBadge isUrgent={referral.is_urgent} />
                            <SrChangeBadge referral={referral} />
                          </>
                        )}
                        {referral.recommended_clinic_name && (
                          <div style={{ marginTop: '3px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '1px 7px',
                              borderRadius: '10px',
                              fontSize: '0.72rem',
                              background: (referral.clinic_recommendation_confidence ?? 100) >= 70 ? '#d1fae5' : (referral.clinic_recommendation_confidence ?? 100) >= 40 ? '#fef3c7' : '#fee2e2',
                              color: (referral.clinic_recommendation_confidence ?? 100) >= 70 ? '#065f46' : (referral.clinic_recommendation_confidence ?? 100) >= 40 ? '#92400e' : '#991b1b',
                            }}>
                              {referral.recommended_clinic_name}
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <SourceBadge source={referral.source} />
                          {referral.is_simulation && <SimulationBadge />}
                        </div>
                      </td>
                      {isAllReferralsView && (
                        <td>{getQueueBucket(referral, currentUserId, userRole)}</td>
                      )}
                      {/* AXIS235: Hide Date of Injury for AE queue */}
                      {!isAllReferralsView && activeTab !== 'ae_queue' && activeTab !== 'awaiting_reply' && (
                        <td>
                          {referral.date_of_injury ? new Date(referral.date_of_injury).toLocaleDateString() : 'N/A'}
                        </td>
                      )}
                      {isAllReferralsView && (
                        <td>
                          {referral.date_of_injury ? new Date(referral.date_of_injury).toLocaleDateString() : 'N/A'}
                        </td>
                      )}
                      {/* AXIS235: AE-specific columns */}
                      {!isAllReferralsView && activeTab === 'ae_queue' && (
                        <td>
                          {referral.days_in_ae_queue || 0}
                        </td>
                      )}
                      {/* AXIS231: Awaiting Reply-specific columns */}
                      {!isAllReferralsView && activeTab === 'awaiting_reply' && (
                        <>
                          <td>
                            {referral.awaiting_reply_at ? new Date(referral.awaiting_reply_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td 
                            style={{ 
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={referral.awaiting_reply_note || ''}
                          >
                            {referral.awaiting_reply_note ? (
                              referral.awaiting_reply_note.length > 80 
                                ? `${referral.awaiting_reply_note.substring(0, 80)}...`
                                : referral.awaiting_reply_note
                            ) : (
                              <span style={{ color: '#999', fontStyle: 'italic' }}>No note</span>
                            )}
                          </td>
                        </>
                      )}
                      <td>
                        {referral.assigned_to_user_id ?
                          (() => {
                            const assignedUser = staffUsers.find(u => u.id === referral.assigned_to_user_id);
                            return assignedUser?.display_name || 'Unknown';
                          })()
                          : 'Unassigned'
                        }
                      </td>
                      <td>
                        <span className={`status-badge status-${referral.status}`}>
                          {referral.status.replace(/_/g, ' ')}
                        </span>
                        {(referral.triage_category_final || referral.triage_category_suggested) && (
                          <TriageCategoryBadge
                            category={referral.triage_category_final || referral.triage_category_suggested}
                            variant="short"
                            showApproved={referral.triage_approved}
                            title="Priority"
                          />
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {loadingReferralId === referral.id ? (
                          <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'center', padding: '4px' }}>
                            <Spinner size="sm" color="secondary" />
                          </div>
                        ) : (
                          <ActionMenu
                            onIconClick={() => handleViewDetail(referral.id)}
                            options={[
                              {
                                label: 'View Details',
                                onClick: () => handleViewDetail(referral.id)
                              },
                              {
                                label: 'Follow-up',
                                onClick: () => handleFollowUp(referral),
                                disabled: isSrReferral(referral) && isSrReferralCancelled(referral),
                              },
                              ...(isSrReferral(referral) &&
                              referral.status !== 'completed' &&
                              referral.status !== 'awaiting_reply'
                                ? [{
                                    label: 'Request Clarification',
                                    onClick: () => handleRequestClarification(referral),
                                    disabled: isSrReferralCancelled(referral),
                                  }]
                                : []),
                              {
                                label: 'Mark Complete',
                                onClick: () => handleMarkComplete(referral),
                                disabled:
                                  (!canMarkCompleteReferral(referral) &&
                                    !canCompleteMiscIcpWithoutIndici(referral)) ||
                                  completing,
                                isLoading: completing,
                                variant: 'success'
                              },
                              ...(userRole === 'admin' ? [{
                                label: 'Delete',
                                onClick: () => handleDelete(referral),
                                disabled: deleting,
                                isLoading: deleting,
                                variant: 'danger' as const
                              }] : [])
                            ]}
                          />
                        )}
                      </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <Button 
            variant="secondary"
            disabled={!pagination?.has_previous || loading}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </Button>
          
          <span className="pagination-info">
            {pagination ? (
              `Page ${currentPage} of ${pagination.total_pages}`
            ) : (
              'Loading...'
            )}
          </span>
          
          <Button 
            variant="secondary"
            disabled={!pagination?.has_next || loading}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>

        {selectedReferral && (
          <FollowUpComposer
            referral={selectedReferral}
            isOpen={showFollowUp}
            onClose={() => {
              setShowFollowUp(false);
              setSelectedReferral(null);
            }}
          />
        )}

        <ReferralDetailModal
          referral={selectedReferral}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedReferral(null);
            deepLinkOpenedRef.current = false;
            syncReferralIdInUrl(null);
            fetchReferrals();
            fetchTabCounts();
          }}
          onReferralUpdate={handleReferralUpdate}
          onCompletionWarning={setCompletionWarning}
          userRole={userRole}
        />

          {/* Bulk Assign Modal */}
          {showBulkAssignModal && (
            <div className="modal-overlay" onClick={() => {
              setShowBulkAssignModal(false);
              setSearchTerm('');
              setShowDropdown(false);
              setSelectedAssigneeId(null);
            }}>
              <div style={{ minHeight: "450px" }} className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>Assign {selectedIds.size} Referral{selectedIds.size !== 1 ? 's' : ''}</h3>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setShowBulkAssignModal(false);
                      setSearchTerm('');
                      setShowDropdown(false);
                      setSelectedAssigneeId(null);
                    }}
                    disabled={bulkAssignLoading}
                    style={{ marginLeft: 'auto' }}
                  >
                    ✕
                  </Button>
                </div>
                <div className="modal-body" style={{ height: '50vh', overflowY: 'auto', paddingBottom: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <p>Select a staff member to assign these referrals to:</p>
                    <div
                      className="dropdown-container"
                      style={{ position: 'relative', marginBottom: '20px' }}
                      ref={dropdownRef}
                    >
                      <input
                        type="text"
                        placeholder="Search staff member..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setShowDropdown(true);
                        }}
                        onFocus={() => setShowDropdown(true)}
                        className="form-control"
                        style={{ width: '100%', padding: '10px', marginBottom: '5px' }}
                        disabled={bulkAssignLoading}
                      />
                      {showDropdown && (
                        <div
                          className="dropdown-menu"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            border: '1px solid #ccc',
                            borderTop: 'none',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            backgroundColor: '#fff',
                            zIndex: 1000,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        >
                          {staffUsers
                            .filter(user =>
                              (userRole === 'admin' || user.id === currentUserId) &&
                              (searchTerm === '' ||
                                (user.display_name?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
                                (user.email?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()))
                            )
                            .map(user => (
                              <div
                                key={user.id}
                                onClick={() => {
                                  setSelectedAssigneeId(user.id);
                                  setSearchTerm('');
                                  setShowDropdown(false);
                                }}
                                style={{
                                  padding: '12px',
                                  cursor: bulkAssignLoading ? 'not-allowed' : 'pointer',
                                  borderBottom: '1px solid #f0f0f0',
                                  opacity: bulkAssignLoading ? 0.6 : 1,
                                  backgroundColor: selectedAssigneeId === user.id ? '#e8f4f8' : '#fff',
                                }}
                                onMouseEnter={(e) => {
                                  if (!bulkAssignLoading && selectedAssigneeId !== user.id) {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f5f5f5';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  (e.currentTarget as HTMLDivElement).style.backgroundColor = selectedAssigneeId === user.id ? '#e8f4f8' : '#fff';
                                }}
                              >
                                <div style={{ fontWeight: 500 }}>{user.display_name}</div>
                                <div style={{ fontSize: '0.85em', color: '#666' }}>{user.email}</div>
                                <div style={{ fontSize: '0.75em', color: '#999' }}>Role: {user.role}</div>
                              </div>
                            ))
                          }
                          {staffUsers.filter(user =>
                            (userRole === 'admin' || user.id === currentUserId) &&
                            (searchTerm === '' ||
                              (user.display_name?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
                              (user.email?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()))
                          ).length === 0 && (
                              <div style={{ padding: '12px', color: '#999', textAlign: 'center' }}>
                                No staff members found
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                    {selectedAssigneeId && (
                      <div style={{ padding: '12px', backgroundColor: '#f0f8ff', borderLeft: '4px solid #4a90e2', marginBottom: '20px' }}>
                        <div style={{ fontWeight: 500, marginBottom: '4px' }}>Selected:</div>
                        <div>{staffUsers.find(u => u.id === selectedAssigneeId)?.display_name}</div>
                        <div style={{ fontSize: '0.85em', color: '#666' }}>{staffUsers.find(u => u.id === selectedAssigneeId)?.email}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <Button
                      variant="primary"
                      onClick={() => {
                        if (selectedAssigneeId) {
                          handleBulkAssign(selectedAssigneeId);
                          setSearchTerm('');
                          setShowDropdown(false);
                          setSelectedAssigneeId(null);
                        }
                      }}
                      disabled={!selectedAssigneeId || bulkAssignLoading}
                      isLoading={bulkAssignLoading}
                    >
                      Assign
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mark Complete Confirmation Dialog */}
          {referralToComplete && isSrReferral(referralToComplete) ? (
            <SRCompleteResponseModal
              referral={referralToComplete}
              isOpen={showCompleteConfirm}
              onClose={() => {
                setShowCompleteConfirm(false);
                setReferralToComplete(null);
                setCompletionError(null);
                setMissingFields([]);
              }}
              onConfirm={(payload) => confirmMarkComplete(payload)}
              isLoading={completing}
              error={completionError}
            />
          ) : referralToComplete ? (
            <ConfirmationModal
              isOpen={showCompleteConfirm}
              title="Mark Referral as Complete"
              message={missingFields.length > 0
                ? "This referral cannot be marked as complete because it is missing required information."
                : getMarkCompleteConfirmCopy(
                    canCompleteMiscIcpWithoutIndici(referralToComplete),
                  ).message}
              confirmLabel={missingFields.length > 0
                ? "Mark Complete"
                : getMarkCompleteConfirmCopy(
                    canCompleteMiscIcpWithoutIndici(referralToComplete),
                  ).confirmLabel}
              onConfirm={() => confirmMarkComplete()}
              onCancel={() => {
                setShowCompleteConfirm(false);
                setReferralToComplete(null);
                setCompletionError(null);
                setMissingFields([]);
              }}
              isLoading={completing}
              variant="success"
              details={[
                { label: 'Patient', value: `${referralToComplete.patient_first_name} ${referralToComplete.patient_last_name}` }
              ]}
              error={completionError}
              additionalContent={missingFields.length > 0 ? (
                <div style={{ marginTop: '16px' }}>
                  <p style={{ fontWeight: 600, marginBottom: '8px', color: '#333' }}>
                    Missing Required Fields:
                  </p>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '20px',
                    color: '#d32f2f',
                    listStyle: 'disc'
                  }}>
                    {missingFields.map((field, index) => (
                      <li key={index} style={{ marginBottom: '4px' }}>
                        {getFieldDisplayLabel(field)}
                      </li>
                    ))}
                  </ul>
                  <p style={{ 
                    marginTop: '12px', 
                    fontSize: '0.9em', 
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    Please edit the referral to populate these fields before marking it as complete.
                  </p>
                </div>
              ) : undefined}
              modalClassName="complete-confirm-modal"
              preventCloseOnOverlayClick={completing}
              confirmDisabled={missingFields.length > 0}
            />
          ) : null}

          {referralToClarify && (
            <SRClarificationModal
              referral={referralToClarify}
              isOpen={showClarificationModal}
              onClose={() => {
                setShowClarificationModal(false);
                setReferralToClarify(null);
              }}
              onSent={async () => {
                await Promise.all([fetchReferrals(), fetchTabCounts()]);
                setActiveTab('awaiting_reply');
              }}
              loadMissingFieldsDraft={(referralId) =>
                ApiService.getSRClarificationDraft(referralId).then((result) => result.body)
              }
              onSend={async (referralId, body, awaitingReplyNote) => {
                await ApiService.requestSRClarification(referralId, body, {
                  awaitingReplyNote,
                });
              }}
            />
          )}

          {/* Delete Confirmation Dialog (AXIS145) */}
          {referralToDelete && (
            <ConfirmationModal
              isOpen={showDeleteConfirm}
              title="Delete Referral"
              message="Are you sure you want to delete this referral? This action cannot be undone. The referral will be removed from the queue and AI triaging UI."
              confirmLabel="Delete"
              onConfirm={confirmDelete}
              onCancel={() => {
                setShowDeleteConfirm(false);
                setReferralToDelete(null);
                setDeleteError(null);
              }}
              isLoading={deleting}
              variant="danger"
              details={[
                { label: 'Patient', value: `${referralToDelete.patient_first_name} ${referralToDelete.patient_last_name}` },
                { label: 'Referral ID', value: referralToDelete.id }
              ]}
              error={deleteError}
              preventCloseOnOverlayClick={deleting}
            />
          )}

          {/* Bulk Delete Confirmation Dialog (AXIS145) */}
          <ConfirmationModal
            isOpen={showBulkDeleteConfirm}
            title="Delete Referrals"
            message={selectedIds.size > 10 
              ? `Are you sure you want to delete ${selectedIds.size} referrals? This action cannot be undone. These referrals will be removed from the queue and AI triaging UI.`
              : `Are you sure you want to delete ${selectedIds.size} referral${selectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.`}
            confirmLabel={`Delete ${selectedIds.size} Referral${selectedIds.size !== 1 ? 's' : ''}`}
            onConfirm={confirmBulkDelete}
            onCancel={() => {
              setShowBulkDeleteConfirm(false);
              setBulkDeleteError(null);
            }}
            isLoading={bulkDeleting}
            variant="danger"
            error={bulkDeleteError}
            preventCloseOnOverlayClick={bulkDeleting}
          />
        </div>
      </div>
    </div>
  );
};
